import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import type {
  Catalog,
  DataResponse,
  ExportFormat,
  ExportItem,
  ExportStarted,
  Labels,
  ListResponse,
  PreviewResult,
  ReportDefinition,
  ReportUser,
  Schedule,
  ScheduleBody,
  SharingBody,
  Template,
  TemplateListItem,
} from './types';

const api = endPoints.reportBuilder;

export const getCatalog = (): Promise<DataResponse<Catalog>> =>
  jwtAxios.get(api.catalog).then((res: { data: DataResponse<Catalog> }) => res.data);

export const getLabels = (params: {
  companyIds: string[];
  taxYear?: string | null;
}): Promise<DataResponse<Labels>> =>
  jwtAxios
    .get(api.labels, {
      params: {
        // Repeated (`companyIds[]=A&companyIds[]=B`): the backend checks each
        // id as a company, so a comma list would be one unknown id.
        companyIds: params.companyIds,
        taxYear: params.taxYear || undefined,
      },
    })
    .then((res: { data: DataResponse<Labels> }) => res.data);

export const getPreview = (
  definition: ReportDefinition,
  signal?: AbortSignal,
): Promise<DataResponse<PreviewResult>> =>
  jwtAxios
    .post(api.preview, { definition }, { signal })
    .then((res: { data: DataResponse<PreviewResult> }) => res.data);

export const getTemplates = (params: {
  page: number;
  size: number;
  search?: string;
}): Promise<ListResponse<TemplateListItem>> =>
  jwtAxios
    .get(api.templates, { params })
    .then((res: { data: ListResponse<TemplateListItem> }) => res.data);

export const getTemplate = (id: string): Promise<DataResponse<Template>> =>
  jwtAxios
    .get(api.template(id))
    .then((res: { data: DataResponse<Template> }) => res.data);

export const createTemplate = (body: {
  name: string;
  definition: ReportDefinition;
}): Promise<DataResponse<Template>> =>
  jwtAxios
    .post(api.templates, body)
    .then((res: { data: DataResponse<Template> }) => res.data);

export const updateTemplate = (
  id: string,
  body: { name: string } | { definition: ReportDefinition },
): Promise<DataResponse<Template>> =>
  jwtAxios
    .put(api.template(id), body)
    .then((res: { data: DataResponse<Template> }) => res.data);

export const deleteTemplate = (id: string): Promise<{ success: boolean }> =>
  jwtAxios
    .delete(api.template(id))
    .then((res: { data: { success: boolean } }) => res.data);

export const getTemplateExports = (
  id: string,
): Promise<DataResponse<ExportItem[]>> =>
  jwtAxios
    .get(api.templateExports(id))
    .then((res: { data: DataResponse<ExportItem[]> }) => res.data);

export const updateTemplateSharing = (
  id: string,
  body: SharingBody,
): Promise<DataResponse<Template>> =>
  jwtAxios
    .put(api.templateSharing(id), body)
    .then((res: { data: DataResponse<Template> }) => res.data);

export const getTemplateSchedule = (
  id: string,
): Promise<DataResponse<Schedule | null>> =>
  jwtAxios
    .get(api.templateSchedule(id))
    .then((res: { data: DataResponse<Schedule | null> }) => res.data);

export const saveTemplateSchedule = (
  id: string,
  body: ScheduleBody,
): Promise<DataResponse<Schedule>> =>
  jwtAxios
    .put(api.templateSchedule(id), body)
    .then((res: { data: DataResponse<Schedule> }) => res.data);

export const deleteTemplateSchedule = (id: string): Promise<void> =>
  jwtAxios.delete(api.templateSchedule(id)).then(() => undefined);

// The people the user may share with or send to: the tenant's admins and
// employer logins, the user left out.
export const getReportUsers = (
  params: { search?: string; limit?: number },
  signal?: AbortSignal,
): Promise<{ data: ReportUser[] }> =>
  jwtAxios
    .get(api.users, { params, signal })
    .then((res: { data: { data: ReportUser[] } }) => res.data);

export const startExport = (body: {
  templateId?: string;
  name: string;
  format: ExportFormat;
  definition: ReportDefinition;
}): Promise<DataResponse<ExportStarted>> =>
  jwtAxios
    .post(api.exports, body)
    .then((res: { data: DataResponse<ExportStarted> }) => res.data);

export const getExportDownload = (
  id: string,
): Promise<DataResponse<{ url: string }>> =>
  jwtAxios
    .get(api.exportDownload(id))
    .then((res: { data: DataResponse<{ url: string }> }) => res.data);

export const isNameClash = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 409;

export const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;
